import React, { useState, useMemo, useEffect, useRef } from 'react';
import Dropdown from 'components/Dropdown/Dropdown';
import { useCustomToast } from 'utils/toastUtils';
import CustomButton from 'components/button/CustomButton';
import CustomIconButton from 'components/button/CustomIconButton';
import { clearGridFocus } from 'utils/gridUtils';
import { useLoader } from "contexts/LoaderContext";

import {
  Box,
  Heading,
  Button,
  Input,
  // Modal, // Replaced by CustomModal
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormLabel,
  FormControl,
  useToast,
  Text,
  Flex,
  IconButton,
  Tooltip,
  Switch,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/CustomModal'; // Import CustomModal
import {
  validateAlphanumericInput,
  projectValidateAlphanumeric,
  ValidateNameField,
  validateDescriptionInput,
} from 'utils/constants';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { fetchTasks, addTasks, EditTasks, deleteTask } from 'utils/api'; // Import API functions
import { MAX_INPUT_LENGTH } from 'utils/constants';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const Taskmanagement = () => {
  // const initialRef = useRef(null); // Removed manual ref
  const showToast = useCustomToast();
  const { showGlobalLoader, hideGlobalLoader } = useLoader();
  // State variables for storing task data
  const [tasks, setTasks] = useState([]); // Stores fetched tasks
  const [selectedTask, setSelectedTask] = useState(null); // Stores selected task for editing or deleting
  // Stores search and filter criteria
  const [filters, setFilters] = useState({
    category: '',
    search: '',
  });
  // Initial structure of a new task
  const initialTaskState = {
    id: tasks.id,
    taskName: '',
    description: '',
    categoryId: '',
    errors: {}, // Stores validation errors
  };
  const [newTask, setNewTask] = useState(initialTaskState);
  // State for handling modals (Add, Edit, Delete)
  const [modalState, setModalState] = useState({
    type: null,
    isOpen: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const gridRef = useRef(null);

  const toast = useToast();
  // Fetch tasks
  useEffect(() => {
    const fetchTaskData = async () => {
      showGlobalLoader();
      try {
        const data = await fetchTasks();
        setTasks(data);
      } catch (error) {
        showToast('error', 'Unable to load tasks from the server.');
      } finally {
        hideGlobalLoader();
      }
    };
    fetchTaskData();
  }, []);
  const [isReadOnly, setIsReadOnly] = useState(false);

  useEffect(() => {
    const accessDetailsString = localStorage.getItem('accessDetails');
    if (accessDetailsString) {
      try {
        const accessDetails = JSON.parse(accessDetailsString);
        const screenAccess = accessDetails.find(
          (screen) => screen.screenId === 106,
        );
        if (screenAccess) {
          setIsReadOnly(screenAccess.readOnly === 1);
        }
      } catch (error) {
        console.error('Invalid accessDetails format in localStorage');
      }
    }
  }, []);

  // Filter tasks based on search and category selection
  const filteredTask = tasks.filter((task) => {
    const matchcategory =
      !filters.category ||
      task.categoryId?.toString() === filters.category.toString();

    const matchesSearch =
      !filters.search ||
      task.taskName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      task.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
      task.categoryName?.toLowerCase().includes(filters.search.toLowerCase());

    return matchcategory && matchesSearch;
  });

  // function for Add new task
  const handleAddTask = async () => {
    let errors = {};

    const taskNameTrimmed = newTask.taskName.trim();

    // Check if empty
    if (!taskNameTrimmed) {
      errors.taskName = 'Task Name is required.';
    }
    // Check if too long
    else if (taskNameTrimmed.length > 30) {
      errors.taskName = 'Task Name must not exceed 30 characters.';
    }
    // Check if contains only special characters
    else if (!ValidateNameField(taskNameTrimmed)) {
      errors.taskName =
        'Task Name must contain at least one character or number.';
    }

    if (!newTask.description.trim()) {
      errors.description = 'Task Description is required.';
    }

    if (!newTask.categoryId) {
      errors.categoryId = 'Task Category is required.';
    }

    // Duplicate task name check (case-insensitive)
    const isDuplicate = tasks.some(
      (task) =>
        task.taskName.trim().toLowerCase() === taskNameTrimmed.toLowerCase() &&
        task.categoryId === newTask.categoryId,
    );
    if (isDuplicate) {
      errors.taskName = 'Task Name already exists.';
    }

    if (Object.keys(errors).length > 0) {
      setNewTask((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      setIsSubmitting(true);
      await addTasks(newTask);
      const data = await fetchTasks();
      setTasks(data);

      showToast('success', `${newTask.taskName} has been added successfully.`);
      setNewTask(initialTaskState); // Reset form
      closeModal();
    } catch (error) {
      showToast(
        'warning',
        error.message || 'Unable to add the task. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    // Disable scroll on the body
    document.body.style.overflow = 'hidden';

    // Clean up: Restore the scroll when the component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  // function for Edit existing task
  const handleEditTask = async () => {
    let errors = {};

    const taskNameTrimmed = newTask.taskName.trim();

    // Check if empty
    if (!taskNameTrimmed) {
      errors.taskName = 'Task Name is required.';
    }
    // Check if contains only special characters
    else if (!ValidateNameField(taskNameTrimmed)) {
      errors.taskName =
        'Task Name must contain at least one character or number.';
    }

    if (!newTask.description.trim()) {
      errors.description = 'Task Description is required.';
    }

    if (!newTask.categoryId) {
      errors.categoryId = 'Task Category is required.';
    }

    // Check for duplicate but exclude current task
    const isDuplicate = tasks.some(
      (task) =>
        task.taskName.trim().toLowerCase() === taskNameTrimmed.toLowerCase() &&
        task.categoryId === newTask.categoryId &&
        task.id !== newTask.id,
    );

    if (isDuplicate) {
      errors.taskName = 'Task Name already exists.';
    }

    if (Object.keys(errors).length > 0) {
      setNewTask((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      setIsSubmitting(true);
      await EditTasks(newTask.id, newTask);
      setTimeout(async () => {
        const data = await fetchTasks();
        setTasks(data);
        clearGridFocus(gridRef);
      }, 100);

      showToast(
        'success',
        `Task '${newTask.taskName}' has been updated successfully.`,
      );
      closeModal();
      setFilters({
        category: '',
        search: '',
      });
    } catch (error) {
      showToast(
        'warning',
        error.message || 'Unable to update the task. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // function for Delete task
  const handleDeleteTask = async () => {
    try {
      setIsSubmitting(true);
      await deleteTask(selectedTask.id);
      setTimeout(async () => {
        const data = await fetchTasks();
        setTasks(data);
        clearGridFocus(gridRef);
      }, 100);
      setTasks(tasks.filter((t) => t.id !== selectedTask.id));

      showToast('success', `Task '${selectedTask.taskName}' has been Deleted.`);
      closeModal();
      setFilters({
        category: '',
        search: '',
      });
    } catch (error) {
      showToast(
        'warning',
        error.message || 'Unable to delete the task. Please try again.',
      );
      closeModal();
      setFilters({
        category: '',
        search: '',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  // coloumn definition
  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Category',
        field: 'categoryName',
        sortable: true,
        flex: 1,
        resizable: false,
      },
      {
        headerName: 'Subcategory',
        field: 'taskName',
        sortable: true,
        flex: 1,
        resizable: false,
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 2,
        resizable: true,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        flex: 1,
        resizable: false,
        cellRenderer: (params) => (
          <Box display="flex" justifyContent="left">
            <CustomIconButton
              iconClass="fas fa-edit"
              tooltip="Edit Task"
              isDisabled={isReadOnly}
              onClick={() => openModal('edit', params.data)}
            />
            <CustomIconButton
              iconClass="fas fa-trash"
              tooltip="Delete Task"
              isDisabled={isReadOnly}
              onClick={() => openModal('delete', params.data)}
            />
          </Box>
        ),
      },
    ],
    [isReadOnly],
  );
  // Open the modal for Add/Edit/Delete operations
  // const openModal = (type, task = null) => {
  //   setModalState({ type, isOpen: true });
  //   if (task) {
  //     setSelectedTask(task);
  //     if (type === 'edit') {
  //       setNewTask(task);
  //     }
  //   }
  // };

  const openModal = (type, task = null) => {
    setModalState({ type, isOpen: true });

    if (task) {
      setSelectedTask({
        ...task,
        id: task.sub_category_Id,
      });

      if (type === 'edit') {
        setNewTask({
          ...task,
          id: task.sub_category_Id,
        });
      }
    }
  };

  // Close the modal and reset form fields
  const closeModal = () => {
    setModalState({ type: null, isOpen: false });
    setNewTask({
      taskName: '',
      description: '',
      categoryId: '',
      // billable: false,
    });
    setSelectedTask(null);
  };

  return (
    <Box minW="100vh" bg="white" minH="94vh" padding='40px 1px 0px 1px'>
      <ToastContainer />
      <Heading mb={5}>Task </Heading>

      {/* Search and Add Task */}
      <Box width="500px" display="flex" mb={4} gap={4}>
        <Dropdown
          type="Categories"
          placeholder="All category"
          selectedValue={filters.category}
          setSelectedValue={(value) =>
            setFilters({ ...filters, category: value })
          }
        />
        <Input
          placeholder="🔍 Search..."
          value={filters.search}
          onChange={(e) => {
            const value = e.target.value;
            if (validateAlphanumericInput(value)) {
              setFilters({ ...filters, search: value });
            }
          }}
          bg="white"
          size="md"
          width="500px"
          variant="outline"
          borderColor="gray.300"
          focusBorderColor="blue.500"
          borderRadius="md"
        />

        <Button
          className="main common-button small"
          isDisabled={isReadOnly}
          onClick={() => openModal('add')}
        >
          Add
        </Button>
      </Box>

      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          rowData={filteredTask}
          columnDefs={columnDefs}
          // defaultColDef={{ resizable: true }}
          pagination={true}
          paginationPageSize={10}
          suppressMovableColumns={true}
          suppressRowDrag={true}
          suppressMoveWhenRowDragging={true}
          paginationPageSizeSelector={[10, 25, 50, 100]}
          context={{ isReadOnly }}
          reactiveCustomComponents={true}
        />
      </div>

      <CustomModal
        isOpen={modalState.isOpen}
        closeOnOverlayClick={false}
        onClose={closeModal}
      // initialFocusRef={initialRef} // handled internally
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalState.type === 'add'
              ? 'Create Subcategory'
              : modalState.type === 'edit'
                ? 'Edit Subcategory'
                : 'Delete Subcategory'}
          </ModalHeader>
          <ModalCloseButton onClick={closeModal} />
          <ModalBody>
            {modalState.type !== 'delete' ? (
              <>
                <FormControl
                  isRequired
                  isInvalid={!!newTask.errors?.categoryId}
                  mb={3}
                // ref={initialRef} // removed
                >
                  <FormLabel fontSize="md" fontWeight="medium">
                    Categories
                  </FormLabel>
                  <Dropdown
                    type="Categories"
                    placeholder="Select category"
                    selectedValue={newTask.categoryId}
                    setSelectedValue={(value) =>
                      setNewTask({
                        ...newTask,
                        categoryId: value,
                        errors: { ...newTask.errors, categoryId: '' },
                      })
                    }
                  />
                  {newTask.errors?.categoryId && (
                    <Text color="red.500" fontSize="sm">
                      {newTask.errors.categoryId}
                    </Text>
                  )}
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={!!newTask.errors?.taskName}
                  mb={3}
                >
                  <FormLabel fontSize="md" fontWeight="medium">
                    Name
                  </FormLabel>
                  <Input
                    placeholder="Subcategory Name"
                    value={newTask.taskName}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow alphabets, numbers, spaces, underscores and hyphens
                      if (/^[a-zA-Z0-9 _-]*$/.test(value) || value === '') {
                        // Set the value and clear any previous errors
                        setNewTask({
                          ...newTask,
                          taskName: value,
                          errors: { ...newTask.errors, taskName: '' },
                        });
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur to check if input is only special characters
                      const value = newTask.taskName.trim();

                      // Check if the value contains at least one alphanumeric character
                      const containsAlphanumeric = /[a-zA-Z0-9]/.test(value);

                      if (value && !containsAlphanumeric) {
                        setNewTask({
                          ...newTask,
                          errors: {
                            ...newTask.errors,
                            taskName:
                              'Task name must contain at least one letter or number.',
                          },
                        });
                      }
                    }}
                    maxLength={30}
                  />

                  {newTask.errors?.taskName && (
                    <Text color="red.500" fontSize="sm">
                      {newTask.errors.taskName}
                    </Text>
                  )}
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={!!newTask.errors?.description}
                  mb={3}
                >
                  <FormLabel fontSize="md" fontWeight="medium">
                    Description
                  </FormLabel>
                  <Input
                    placeholder="Subcategory Description"
                    value={newTask.description}
                    onChange={(e) => {
                      if (validateDescriptionInput(e.target.value)) {
                        setNewTask({
                          ...newTask,
                          description: e.target.value,
                          errors: { ...newTask.errors, description: '' },
                        });
                      }
                    }}
                  />
                  {newTask.errors?.description && (
                    <Text color="red.500" fontSize="sm">
                      {newTask.errors.description}
                    </Text>
                  )}
                </FormControl>

                {/* <Box mt={3} display={'flex'} alignItems={'space-between'}>
                  <Text fontSize="md">Billable Task</Text>
                  <Switch
                    marginLeft={2}
                    isChecked={newTask.billable}
                    onChange={(e) =>
                      setNewTask({ ...newTask, billable: e.target.checked })
                    }
                  />
                </Box> */}
              </>
            ) : (
              <p>
                Are you sure you want to delete the task "
                {selectedTask.taskName}"?
              </p>
            )}
          </ModalBody>

          <ModalFooter>
            {modalState.type === 'delete' ? (
              <Flex gap={4} justify="flex-end" mt={3}>
                <CustomButton
                  label="Cancel"
                  onClick={closeModal}
                  variant="outline"
                  colorScheme="black"
                  className="common-button"
                />
                <CustomButton
                  label="Delete"
                  onClick={handleDeleteTask}
                  colorScheme="red"
                  className="common-button"
                  isLoading={isSubmitting}
                />
              </Flex>
            ) : (
              <Flex gap={4} mt={3}>
                <CustomButton
                  label="Cancel"
                  onClick={closeModal}
                  variant="outline"
                  colorScheme="black"
                  className="common-button"
                />
                <CustomButton
                  label={modalState.type === 'edit' ? 'Save' : 'Save'}
                  isDisabled={isReadOnly}
                  onClick={
                    modalState.type === 'edit' ? handleEditTask : handleAddTask
                  }
                  colorScheme={modalState.type === 'edit' ? 'green' : 'green'}
                  className="common-button"
                  isLoading={isSubmitting}
                />
              </Flex>
            )}
          </ModalFooter>
        </ModalContent>
      </CustomModal>
    </Box>
  );
};

export default Taskmanagement;
